import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  LogOut,
  ArrowUpRight,
  Mail,
  Phone,
  Building2,
  Clock,
  Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth, authHeader } from "@/lib/auth";
import { LOGO } from "@/lib/brand";

const STATUSES = ["all", "new", "contacted", "quoted", "won", "archived"];

const STATUS_COLOR = {
  new: "var(--ap-magenta)",
  contacted: "var(--ap-cyan)",
  quoted: "var(--ap-yellow)",
  won: "var(--ap-gold)",
  archived: "#999999",
};

function StatCard({ label, value, testId }) {
  return (
    <div className="border-r border-b border-black p-6 md:p-8" data-testid={testId}>
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60">
        {label}
      </div>
      <div className="font-display text-5xl md:text-6xl leading-none mt-3 tabular-nums">
        {value}
      </div>
    </div>
  );
}

export default function AdminQuotes() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, quoted: 0, won: 0 });
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        api.get(`/admin/quotes`, {
          params: { q: q || undefined, status },
          headers: authHeader(),
        }),
        api.get(`/admin/quotes/stats`, { headers: authHeader() }),
      ]);
      setItems(list.data);
      setStats(s.data);
      if (selected) {
        const still = list.data.find((x) => x.id === selected.id);
        setSelected(still || null);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Failed to load quotes.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const changeStatus = async (id, next) => {
    setSavingId(id);
    try {
      await api.patch(`/admin/quotes/${id}`, { status: next }, { headers: authHeader() });
      toast.success(`Marked as ${next}.`);
      await load();
    } catch (e) {
      toast.error("Update failed.");
    } finally {
      setSavingId(null);
    }
  };

  const totalDisplay = useMemo(() => stats.total || 0, [stats]);

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-black paper-grain">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-[#F4F4F0]/90 backdrop-blur-md border-b border-black">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-3">
            <img src={LOGO.mark} alt="Arvind Printers" className="h-9 w-auto" />
            <div className="hidden sm:block leading-none">
              <div className="font-display text-lg">Arvind Printers</div>
              <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-black/60 mt-1">
                Admin · Quotes Console
              </div>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <div className="hidden md:block font-mono text-[10px] tracking-[0.3em] uppercase text-black/60">
              {user?.email}
            </div>
            <button
              type="button"
              onClick={() => {
                logout();
                navigate("/admin/login", { replace: true });
              }}
              data-testid="admin-logout"
              className="btn-offset"
            >
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-10 md:py-14">
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60">
              Command Centre / Quotes
            </div>
            <h1 className="font-display text-5xl md:text-6xl leading-[0.98] tracking-tight mt-2">
              Every brief. <em className="italic">In one press.</em>
            </h1>
          </div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60 hidden md:block">
            {totalDisplay} total submissions
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 border-l border-t border-black mb-10">
          <StatCard label="Total" value={stats.total} testId="stat-total" />
          <StatCard label="New" value={stats.new} testId="stat-new" />
          <StatCard label="Contacted" value={stats.contacted} testId="stat-contacted" />
          <StatCard label="Quoted" value={stats.quoted} testId="stat-quoted" />
          <StatCard label="Won" value={stats.won} testId="stat-won" />
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                data-testid={`filter-${s}`}
                className={`border border-black px-3 py-2 font-mono text-[10px] tracking-[0.25em] uppercase transition-colors ${
                  status === s
                    ? "bg-black text-[#F4F4F0]"
                    : "bg-transparent hover:bg-black hover:text-[#F4F4F0]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <form onSubmit={onSearch} className="flex items-center gap-2">
            <div className="flex items-center border border-black">
              <Search className="w-4 h-4 mx-3 opacity-70" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email, company…"
                className="bg-transparent outline-none py-2 pr-3 font-mono text-[12px] w-[280px] max-w-full"
                data-testid="admin-search-input"
              />
            </div>
            <button type="submit" className="btn-offset" data-testid="admin-search-submit">
              Filter
            </button>
          </form>
        </div>

        {/* List + detail */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 border border-black bg-[#F4F4F0]">
            <div className="grid grid-cols-12 border-b border-black py-3 px-4 font-mono text-[10px] tracking-[0.25em] uppercase text-black/60">
              <div className="col-span-4">Client</div>
              <div className="col-span-3">Service</div>
              <div className="col-span-3 hidden md:block">Received</div>
              <div className="col-span-2 text-right">Status</div>
            </div>
            {loading ? (
              <div className="p-10 flex items-center gap-3 text-black/60 font-mono text-[11px] tracking-wider">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading briefs…
              </div>
            ) : items.length === 0 ? (
              <div className="p-10 font-mono text-[11px] tracking-[0.25em] uppercase text-black/60">
                No quotes match this filter.
              </div>
            ) : (
              <ul className="max-h-[62vh] overflow-y-auto">
                {items.map((it) => {
                  const isActive = selected?.id === it.id;
                  return (
                    <li key={it.id}>
                      <button
                        type="button"
                        onClick={() => setSelected(it)}
                        data-testid={`quote-row-${it.id}`}
                        className={`w-full text-left grid grid-cols-12 items-center py-4 px-4 border-b border-black/15 hover:bg-black/[0.03] transition-colors ${
                          isActive ? "bg-black/[0.05]" : ""
                        }`}
                      >
                        <div className="col-span-4">
                          <div className="font-display text-lg md:text-xl leading-tight">
                            {it.name}
                          </div>
                          <div className="font-mono text-[11px] text-black/60 truncate">
                            {it.email}
                            {it.company ? ` · ${it.company}` : ""}
                          </div>
                        </div>
                        <div className="col-span-3 font-mono text-[11px] uppercase tracking-wider truncate">
                          {it.service}
                        </div>
                        <div className="col-span-3 font-mono text-[10px] tracking-wider text-black/60 hidden md:block">
                          {new Date(it.created_at).toLocaleString()}
                        </div>
                        <div className="col-span-2 flex justify-end">
                          <span
                            className="inline-block px-2 py-1 border border-black font-mono text-[10px] tracking-[0.2em] uppercase"
                            style={{
                              background: STATUS_COLOR[it.status || "new"] || "#eee",
                              color: it.status === "won" ? "#0A0A0A" : "#0A0A0A",
                            }}
                          >
                            {it.status || "new"}
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Detail */}
          <div className="lg:col-span-5">
            {!selected ? (
              <div className="border border-dashed border-black/40 p-10 min-h-[300px] flex items-center justify-center text-center">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60">
                    Nothing selected
                  </div>
                  <div className="font-display text-2xl md:text-3xl mt-3 italic">
                    Pick a brief to read the story.
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-black bg-[#F4F4F0] p-6 md:p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60">
                      Brief #{selected.id.slice(0, 8)}
                    </div>
                    <div className="font-display text-3xl md:text-4xl mt-1 leading-tight">
                      {selected.name}
                    </div>
                    <div className="font-mono text-[11px] tracking-wider text-black/60 mt-2 flex items-center gap-3">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(selected.created_at).toLocaleString()}
                    </div>
                  </div>
                  <span
                    className="inline-block px-2 py-1 border border-black font-mono text-[10px] tracking-[0.2em] uppercase"
                    style={{ background: STATUS_COLOR[selected.status || "new"] }}
                  >
                    {selected.status || "new"}
                  </span>
                </div>

                <div className="border-t border-black pt-4 space-y-3 font-mono text-[12px]">
                  <a
                    href={`mailto:${selected.email}`}
                    className="flex items-center gap-3 hover:text-[color:var(--ap-magenta)]"
                    data-testid={`quote-reply-mail-${selected.id}`}
                  >
                    <Mail className="w-3.5 h-3.5" /> {selected.email}
                  </a>
                  {selected.phone && (
                    <a
                      href={`tel:${selected.phone}`}
                      className="flex items-center gap-3 hover:text-[color:var(--ap-cyan)]"
                    >
                      <Phone className="w-3.5 h-3.5" /> {selected.phone}
                    </a>
                  )}
                  {selected.company && (
                    <div className="flex items-center gap-3">
                      <Building2 className="w-3.5 h-3.5" /> {selected.company}
                    </div>
                  )}
                </div>

                <div className="border-t border-black mt-6 pt-4">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60 mb-2">
                    Service
                  </div>
                  <div className="font-display text-xl md:text-2xl">{selected.service}</div>
                  {selected.quantity && (
                    <div className="font-mono text-[11px] text-black/60 mt-1">
                      Quantity: {selected.quantity}
                    </div>
                  )}
                </div>

                <div className="border-t border-black mt-6 pt-4">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60 mb-2">
                    Brief
                  </div>
                  <p className="whitespace-pre-wrap text-[14px] leading-relaxed">
                    {selected.message}
                  </p>
                </div>

                <div className="border-t border-black mt-6 pt-4">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60 mb-3">
                    Update Status
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {["new", "contacted", "quoted", "won", "archived"].map((s) => (
                      <button
                        key={s}
                        onClick={() => changeStatus(selected.id, s)}
                        disabled={savingId === selected.id}
                        data-testid={`status-${s}-${selected.id}`}
                        className={`border border-black px-3 py-2 font-mono text-[10px] tracking-[0.25em] uppercase transition-colors ${
                          selected.status === s
                            ? "bg-black text-[#F4F4F0]"
                            : "bg-transparent hover:bg-black hover:text-[#F4F4F0]"
                        } disabled:opacity-50`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-3">
                  <a
                    href={`mailto:${selected.email}?subject=Re:%20Your%20${encodeURIComponent(
                      selected.service
                    )}%20brief%20—%20Arvind%20Printers`}
                    className="btn-offset"
                    data-testid={`quote-reply-cta-${selected.id}`}
                  >
                    Reply by email <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60">
                    Email delivered:{" "}
                    <span
                      style={{
                        color: selected.email_sent
                          ? "var(--ap-magenta)"
                          : "#999",
                      }}
                    >
                      {selected.email_sent ? "yes" : "no"}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
